import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Title, Text, Button, Modal, TextInput, Group, Stack, ActionIcon, Grid, Card, Badge } from '@mantine/core';
import { useForm } from '@mantine/form';
// import { useClipboard } from '@mantine/hooks';
import { IconPlus, IconTrash, IconUsers, IconSearch, IconLink, IconCalendar, IconChevronRight, IconCopyPlus } from '@tabler/icons-react';
import { auth, db } from '../firebase';
import { collection, query, where, documentId, getDocs, getDoc, addDoc, doc, deleteDoc, updateDoc, setDoc, Timestamp, writeBatch, arrayUnion } from 'firebase/firestore';
import { toast } from '../ui/toast';
import { confirm } from '../ui/confirm';
import { PlanService } from '../services/billing/PlanService';
import { useNavigate } from 'react-router-dom';
// import CalendarCard from '../components/dashboard/CalendarCard';
import UpcomingEvents from '../components/dashboard/UpcomingEvents';
import ManageMembersModal from '../components/modals/ManageMembersModal';
import useMembersManager from '../hooks/useMembersManager';
import ShareCalendarModal from '../components/modals/ShareCalendarModal';
import { useTranslation } from 'react-i18next';

export default function Dashboard() {
  const { t } = useTranslation('common');
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [plannings, setPlannings] = useState([]);
  const [filter, setFilter] = useState('');
  const [addModalOpened, setAddModalOpened] = useState(false);
  const addForm = useForm({ initialValues: { name: '' }, validate: { name: (value) => (value ? null : t('dashboard.errors.name_required')) } });
  // Join calendar by share code
  const [joinModalOpened, setJoinModalOpened] = useState(false);
  // Modal and state for displaying share code
  const [shareModalOpened, setShareModalOpened] = useState(false);
  const [shareCode, setShareCode] = useState('');
  // const clipboard = useClipboard({ timeout: 2000 });
  const joinForm = useForm({ initialValues: { code: '' }, validate: { code: (value) => (value ? null : t('dashboard.errors.code_required')) } });
  // State for participants modal
  const [participantsModalOpened, setParticipantsModalOpened] = useState(false);
  const [currentPlanningId, setCurrentPlanningId] = useState(null);
  

  const mm = useMembersManager({ db, planningId: currentPlanningId, currentUserId: user?.uid });

  const fetchPlannings = useCallback(async () => {
    if (!user) return;
    // Owned (legacy single owner)
    const ownerQ = query(collection(db, 'plannings'), where('owner', '==', user.uid));
    const ownerSnap = await getDocs(ownerQ);
    const ownerList = ownerSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // Owned (multi-owner)
    let coOwnerList = [];
    try {
      const ownersQ = query(collection(db, 'plannings'), where('owners', 'array-contains', user.uid));
      const ownersSnap = await getDocs(ownersQ);
      coOwnerList = ownersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (_) {}
    const partQ = query(collection(db, 'plannings'), where('participants', 'array-contains', user.uid));
    const partSnap = await getDocs(partQ);
    const partList = partSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => p.owner !== user.uid && !(Array.isArray(p.owners) && p.owners.includes(user.uid)));
    // Merge and de-duplicate by id
    const map = new Map();
    [...ownerList, ...coOwnerList, ...partList].forEach((p) => map.set(p.id, p));
    setPlannings(Array.from(map.values()));
  }, [user]);

  useEffect(() => {
    fetchPlannings();
  }, [fetchPlannings]);
  // Migration no longer needed; disabled
  // Ensure user profile exists in Firestore and has a default plan document
  useEffect(() => {
    if (!user) return;
    (async () => {
      const profileRef = doc(db, 'users', user.uid);
      const snap = await getDoc(profileRef);
      if (!snap.exists()) {
        await setDoc(profileRef, {
          email: user.email || null,
          displayName: user.displayName || null,
        }, { merge: true });
      }
      // Always keep basic identity fresh
      await setDoc(profileRef, { email: user.email || null, displayName: user.displayName || null }, { merge: true });
      // Ensure there is at least one plan doc; create default FREE effective now otherwise
      await PlanService.ensureDefaultFreePlan(user.uid);
      // Backfill removed (already executed)
    })();
  }, [user]);

  const handleAdd = async (values) => {
    if (!user) return;
    // Enforce creator plan calendar cap
    try {
      const plan = await PlanService.getCurrentPlan(user.uid);
      const { maxCalendars } = PlanService.getLimits(plan);
      const ownedCount = plannings.filter((p) => p.owner === user.uid).length;
      if (ownedCount >= maxCalendars) {
        toast.show({ title: 'Plan limit', message: 'Your plan does not allow creating more calendars', color: 'red' });
        return;
      }
    } catch {}
    await addDoc(collection(db, 'plannings'), { name: values.name, owner: user.uid, owners: [user.uid], participants: [], createdAt: new Date() });
    setAddModalOpened(false);
    addForm.reset();
    fetchPlannings();
  };

  const handleDelete = async (id) => {
    if (!user) return;
    const planning = plannings.find((p) => p.id === id);
    const ownersArr = Array.isArray(planning?.owners) ? planning.owners : [];
    const isOwner = planning?.owner === user.uid || ownersArr.includes(user.uid);
    if (!isOwner) {
      toast.show({ title: 'Permission denied', message: 'Only the owner can delete this calendar' });
      return;
    }
    if (await confirm({ title: 'Delete Calendar', message: 'Are you sure you want to delete this calendar?', color: 'red' })) {
      await deleteDoc(doc(db, 'plannings', id));
      fetchPlannings();
    }
  };

  const handleView = (id) => {
    navigate(`/planning/${id}`);
  };

  const handleDuplicate = async (id) => {
    if (!user) return;
    try {
      // Enforce creator plan calendar cap for the duplicating user
      try {
        const plan = await PlanService.getCurrentPlan(user.uid);
        const { maxCalendars, maxMembersPerCalendar } = PlanService.getLimits(plan);
        const ownedCount = plannings.filter((p) => p.owner === user.uid || (Array.isArray(p.owners) && p.owners.includes(user.uid))).length;
        if (ownedCount >= maxCalendars) {
          toast.show({ title: 'Plan limit', message: 'Your plan does not allow creating more calendars', color: 'red' });
          return;
        }
        // We'll also use maxMembersPerCalendar below
        var ownerMemberCap = maxMembersPerCalendar;
      } catch (_) {}

      const srcRef = doc(db, 'plannings', id);
      const srcSnap = await getDoc(srcRef);
      if (!srcSnap.exists()) return;
      const src = srcSnap.data() || {};

      const origOwners = Array.isArray(src.owners) && src.owners.length ? Array.from(new Set(src.owners.filter(Boolean))) : (src.owner ? [src.owner] : []);
      const origParticipants = Array.isArray(src.participants) ? src.participants.filter(Boolean) : [];
      const allMembers = Array.from(new Set([...(origOwners || []), ...(origParticipants || [])]));

      // New calendar will be owned by current user; others become participants
      const newOwners = [user.uid];
      const newParticipants = allMembers.filter((uid) => uid && uid !== user.uid);

      // Preload source members (including ghosts) to properly evaluate member cap and prepare mapping
      let srcMembersSnap = null;
      try {
        srcMembersSnap = await getDocs(collection(db, 'plannings', id, 'members'));
      } catch (_) {}

      // Determine which member docs are real users vs ghosts
      const srcMemberIds = srcMembersSnap && !srcMembersSnap.empty ? srcMembersSnap.docs.map((d) => d.id) : [];
      const realUserIdSet = new Set();
      try {
        // Firestore 'in' operator supports batches (limit ~10 per query); chunk to be safe
        const chunk = (arr, size) => arr.reduce((acc, _, i) => (i % size ? acc : [...acc, arr.slice(i, i + size)]), []);
        for (const ids of chunk(srcMemberIds, 10)) {
          if (!ids.length) continue;
          const usersQ = query(collection(db, 'users'), where(documentId(), 'in', ids));
          const usersSnap = await getDocs(usersQ);
          usersSnap.docs.forEach((d) => realUserIdSet.add(d.id));
        }
      } catch (_) {}

      // Build mapping for ghosts: oldId -> newId (new random doc ids in destination)
      const ghostOldIds = srcMemberIds.filter((uid) => !realUserIdSet.has(uid));
      const ghostIdMap = new Map();
      try {
        for (const oldId of ghostOldIds) {
          const newRef = doc(collection(db, 'plannings', 'tmp', 'members')); // temp to mint an id; will override path below
          // We only need a new random id; replace path when writing
          ghostIdMap.set(oldId, newRef.id);
        }
      } catch (_) {}

      // Check member cap if available from plan limits (owner + unique participants + ghosts as new IDs)
      try {
        if (Number.isFinite(ownerMemberCap)) {
          const newGhostIds = Array.from(ghostIdMap.values());
          const fullParticipants = Array.from(new Set([...(newParticipants || []), ...Array.from(realUserIdSet), ...newGhostIds].filter(Boolean)));
          const totalMembers = 1 + fullParticipants.length; // owner + participants
          if (totalMembers > ownerMemberCap) {
            toast.show({ title: 'Plan limit', message: 'Duplicated calendar would exceed your plan’s member limit', color: 'red' });
            return;
          }
        }
      } catch (_) {}

      // Copy selected settings from source planning (optimization-related keys)
      const settings = {};
      Object.keys(src || {}).forEach((k) => {
        if (k === 'shareCode') return; // do not carry over share code
        if (k.startsWith('optimization') || k === 'timezone') settings[k] = src[k];
      });

      const newName = src.name ? `${src.name} (Copy)` : 'Planning (Copy)';

      // 1) Create new planning doc with empty participants to satisfy rules
      const dstRef = await addDoc(collection(db, 'plannings'), {
        name: newName,
        owner: user.uid,
        owners: newOwners,
        participants: [],
        createdAt: new Date(),
        ...settings,
      });

      // Build full participants list including ghosts (with NEW ids) so member writes are allowed by rules
      try {
        const newGhostIds = Array.from(ghostIdMap.values());
        const full = Array.from(new Set([...(newParticipants || []), ...Array.from(realUserIdSet), ...newGhostIds].filter(Boolean)));
        await updateDoc(dstRef, { participants: full });
      } catch (_) {}

      // 2) Copy members subcollection (actual and ghost) — only allowed fields
      try {
        if (srcMembersSnap && !srcMembersSnap.empty) {
          const batch = writeBatch(db);
          srcMembersSnap.docs.forEach((d) => {
            const data = d.data() || {};
            const allowed = {};
            // Roles
            if (Array.isArray(data.roles)) allowed.roles = data.roles;
            else allowed.roles = [];
            // Contract
            if (data.contract === 'external' || data.contract === 'internal') allowed.contract = data.contract;
            else allowed.contract = 'internal';
            // Weekly cap: only if valid per rules (1..168)
            const wh = Number(data.weeklyHoursMax);
            if (Number.isFinite(wh) && wh >= 1 && wh <= 168) allowed.weeklyHoursMax = Math.round(wh);
            // Display name: ensure a non-empty string (fallback to uid)
            const fallbackName = d.id;
            if (typeof data.displayName === 'string' && data.displayName.trim()) allowed.displayName = String(data.displayName).trim().slice(0, 100);
            else allowed.displayName = fallbackName;
            if (data.joinedAt && typeof data.joinedAt.toDate === 'function') allowed.joinedAt = data.joinedAt;
            // Resolve destination id (preserve real users; generate new ids for ghosts)
            const destId = realUserIdSet.has(d.id) ? d.id : (ghostIdMap.get(d.id) || d.id);
            const mRef = doc(db, 'plannings', dstRef.id, 'members', destId);
            batch.set(mRef, allowed);
          });
          await batch.commit();
        }
      } catch (e) {
        console.warn('Member copy failed', e);
        // Fallback: try sequential writes with arrayUnion to safely satisfy isKnownMember
        try {
          if (srcMembersSnap && !srcMembersSnap.empty) {
            for (const d of srcMembersSnap.docs) {
              const data = d.data() || {};
              // Resolve destination id (preserve real users; generate new ids for ghosts)
              const uid = realUserIdSet.has(d.id) ? d.id : (ghostIdMap.get(d.id) || d.id);
              // Ensure uid is listed as participant (in case previous update failed)
              try { await updateDoc(dstRef, { participants: arrayUnion(uid) }); } catch (_) {}
              const allowed = {};
              allowed.roles = Array.isArray(data.roles) ? data.roles : [];
              allowed.contract = (data.contract === 'external' || data.contract === 'internal') ? data.contract : 'internal';
              const wh = Number(data.weeklyHoursMax);
              if (Number.isFinite(wh) && wh >= 1 && wh <= 168) allowed.weeklyHoursMax = Math.round(wh);
              const fallbackName = uid;
              allowed.displayName = (typeof data.displayName === 'string' && data.displayName.trim()) ? String(data.displayName).trim().slice(0, 100) : fallbackName;
              if (data.joinedAt && typeof data.joinedAt.toDate === 'function') allowed.joinedAt = data.joinedAt;
              const mRef = doc(db, 'plannings', dstRef.id, 'members', uid);
              try { await setDoc(mRef, allowed, { merge: true }); } catch (_) {}
            }
          }
        } catch (_) {}
      }

      // 3) Copy events without allocations/assignments and stamp createdBy = current user
      try {
        const srcEventsSnap = await getDocs(collection(db, 'plannings', id, 'events'));
        if (!srcEventsSnap.empty) {
          const batch = writeBatch(db);
          srcEventsSnap.docs.forEach((d) => {
            const data = d.data() || {};
            const { assignments, assignedTo, createdBy: _cb, ...rest } = data;
            // Ensure required fields match rules (description must be string)
            const desc = typeof rest.description === 'string' ? rest.description : '';
            const newEventRef = doc(collection(db, 'plannings', dstRef.id, 'events'));
            batch.set(newEventRef, { ...rest, description: desc, assignments: [], assignedTo: null, createdBy: user.uid });
          });
          await batch.commit();
        }
      } catch (e) {
        console.warn('Event copy failed', e);
      }

      toast.show({ title: 'Duplicated', message: 'Calendar duplicated successfully', color: 'green' });
      fetchPlannings();
    } catch (e) {
      console.error('Duplicate failed', e);
      toast.show({ title: 'Failed', message: 'Could not duplicate calendar', color: 'red' });
    }
  };

  // Show participants in a modal (live via hook)
  const handleShowUsers = async (id) => {
    setCurrentPlanningId(id);
    setParticipantsModalOpened(true);
  };

  // Removed local removeParticipant; ManageMembersModal uses mm.removeParticipant

  const handleShare = async (id) => {
    if (!user) return;
    const planning = plannings.find((p) => p.id === id);
    if (planning?.owner !== user.uid) {
      toast.show({ title: 'Not allowed', message: 'Only the owner can share this calendar', color: 'red' });
      return;
    }
    const docRef = doc(db, 'plannings', id);
    try {
      // Enforce owner plan cap on members before sharing
      const ownerPlan = await PlanService.getCurrentPlan(planning.owner);
      const { maxMembersPerCalendar } = PlanService.getLimits(ownerPlan);
      const membersCount = 1 + ((Array.isArray(planning.participants) ? planning.participants.length : 0));
      if (membersCount >= maxMembersPerCalendar) {
        toast.show({ title: 'Plan limit reached', message: 'Your plan does not allow inviting more people to this calendar', color: 'red' });
        return;
      }
      const code = id;
      await updateDoc(docRef, { shareCode: code });
      setShareCode(code);
      setShareModalOpened(true);
    } catch (err) {
      console.error('Failed to set share code', err);
    }
  };
  
  // Join a calendar using a share code
  const handleJoin = async (values) => {
    if (!user) return;
    const code = values.code.trim();
    console.log("Joining with share code:", code);
    // Find planning with matching document ID (share code)
    const codeQ = query(collection(db, 'plannings'), where(documentId(), '==', code));
    const snap = await getDocs(codeQ);
    if (snap.empty) {
      toast.show({ title: 'Invalid share code', message: 'Please check the code and try again', color: 'red' });
    } else {
      const docSnap = snap.docs[0];
      const data = docSnap.data();
      // Enforce owner plan cap before join request
      try {
        const ownerPlan = await PlanService.getCurrentPlan(data.owner);
        const { maxMembersPerCalendar } = PlanService.getLimits(ownerPlan);
        const membersCount = 1 + ((Array.isArray(data.participants) ? data.participants.length : 0));
        if (membersCount >= maxMembersPerCalendar) {
          toast.show({ title: 'Plan limit reached', message: 'This calendar is at capacity for the owner’s plan', color: 'red' });
          return;
        }
      } catch {}
      // Check if already owner or participant
      if (data.owner === user.uid || (data.participants || []).includes(user.uid)) {
        toast.show({ title: 'Already a member', message: 'You are already part of this calendar', color: 'yellow' });
      } else {
        try {
          const reqRef = doc(db, 'plannings', docSnap.id, 'joinRequests', user.uid);
          const existing = await getDoc(reqRef);
          if (existing.exists()) {
            const r = existing.data();
            if (r.status === 'pending') {
              toast.show({ title: 'Request pending', message: 'The owner will approve you soon', color: 'yellow' });
              
            } else if (r.status === 'rejected') {
              // Re-request by setting back to pending
              await updateDoc(reqRef, { status: 'pending', createdAt: Timestamp.fromDate(new Date()) });
              toast.show({ title: 'Request resent', message: 'The owner will approve you soon', color: 'blue' });
              
            } else if (r.status === 'approved') {
              toast.show({ title: 'Already approved', message: 'You were already approved. Please refresh.' , color: 'green' });
            }
          } else {
            await setDoc(reqRef, {
              uid: user.uid,
              email: user.email || null,
              displayName: user.displayName || null,
              status: 'pending',
              createdAt: Timestamp.fromDate(new Date()),
            });
            toast.show({ title: 'Request sent', message: 'Waiting for owner approval', color: 'blue' });
            
          }
        } catch (e) {
          console.error('Failed to create join request', e);
          toast.show({ title: 'Error', message: 'Failed to send request', color: 'red' });
        } finally {
          setJoinModalOpened(false);
          joinForm.reset();
        }
      }
    }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return plannings;
    return plannings.filter((p) => String(p.name || '').toLowerCase().includes(q));
  }, [plannings, filter]);

  return (
    <Container size="xl" px="sm">
      {/* Header */}
      <Group align="end" justify="space-between" mt="md" mb="md">
        <Stack gap={4}>
          <Title order={2}>{t('dashboard.title', { name: user?.displayName || user?.email })}</Title>
          <Text c="dimmed" size="sm">{t('dashboard.subtitle')}</Text>
        </Stack>
        <Group gap="xs">
          <Button variant="outline" leftSection={<IconSearch size={16} />} onClick={() => setJoinModalOpened(true)}>
            {t('dashboard.join_button')}
          </Button>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setAddModalOpened(true)}>
            {t('dashboard.new_button')}
          </Button>
        </Group>
      </Group>

      {/* Calendars */}
      <Stack gap="sm" mb="md">
        <Group justify="space-between" align="center">
          <Text fw={600}>{t('dashboard.your_calendars')}</Text>
          <TextInput
            placeholder={t('dashboard.search_placeholder')}
            rightSection={<IconSearch size={16} />}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            style={{ width: 240 }}
          />
        </Group>
        <Grid gutter="sm">
          {filtered.map((p) => {
            const ownersArr = Array.isArray(p.owners) && p.owners.length ? p.owners : (p.owner ? [p.owner] : []);
            const isOwner = ownersArr.includes(user.uid);
            const members = (Array.isArray(p.participants) ? p.participants.length : 0) + (ownersArr.length || 0);
            return (
              <Grid.Col key={p.id} span={{ base: 12, sm: 6, lg: 4 }}>
                <Card withBorder radius="md" shadow="sm" p="md" onClick={() => handleView(p.id)} style={{ cursor: 'pointer' }}>
                  <Stack gap={8}>
                    <Group justify="space-between" align="flex-start">
                      <Group gap={8} align="center" style={{ minWidth: 0 }}>
                        <IconCalendar size={18} />
                        <Text fw={600} truncate>{p.name || t('dashboard.untitled')}</Text>
                      </Group>
                      <Badge color={isOwner ? 'green' : 'gray'} variant={isOwner ? 'light' : 'outline'}>{isOwner ? t('dashboard.owner_badge') : t('dashboard.member_badge')}</Badge>
                    </Group>
                    <Group gap={12} c="dimmed" size="sm">
                      <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<IconUsers size={14} />}
                        onClick={(e) => { e.stopPropagation(); handleShowUsers(p.id); }}
                        title={t('dashboard.view_members')}
                      >
                        {members}
                      </Button>
                    </Group>
                    <Group gap={6}>
                      <Button variant="light" onClick={(e) => { e.stopPropagation(); handleView(p.id); }} rightSection={<IconChevronRight size={16} />}>{t('dashboard.open')}</Button>
                      <ActionIcon variant="default" onClick={(e) => { e.stopPropagation(); handleDuplicate(p.id); }} title={t('dashboard.duplicate', { defaultValue: 'Duplicate' })}>
                        <IconCopyPlus size={16} />
                      </ActionIcon>
                      <ActionIcon variant="default" onClick={(e) => { e.stopPropagation(); handleShare(p.id); }} title={t('dashboard.copy_share_link')}>
                        <IconLink size={16} />
                      </ActionIcon>
                      {isOwner && (
                        <ActionIcon variant="default" color="red" onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} title={t('dashboard.delete')}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Stack>
                </Card>
              </Grid.Col>
            );
          })}
        </Grid>
        {filtered.length === 0 && (
          <Text c="dimmed" size="sm">{t('dashboard.empty_search')}</Text>
        )}
      </Stack>

      {/* Upcoming events */}
      <UpcomingEvents user={user} plannings={plannings} />
      <Modal opened={addModalOpened} onClose={() => setAddModalOpened(false)} title={t('dashboard.add_modal_title')}>
        <form onSubmit={addForm.onSubmit(handleAdd)}>
          <TextInput label={t('dashboard.add_modal_name_label')} required {...addForm.getInputProps('name')} />
          <Button type="submit" mt="md">
            {t('dashboard.add_modal_create')}
          </Button>
        </form>
      </Modal>
      {/* Modal for joining a calendar via share code */}
      <Modal opened={joinModalOpened} onClose={() => setJoinModalOpened(false)} title={t('dashboard.join_modal_title')}>
        <form onSubmit={joinForm.onSubmit(handleJoin)}>
          <TextInput label={t('dashboard.join_modal_label')} required {...joinForm.getInputProps('code')} />
          <Button type="submit" mt="md">
            {t('dashboard.join_modal_join')}
          </Button>
        </form>
      </Modal>

      <ShareCalendarModal opened={shareModalOpened} onClose={() => setShareModalOpened(false)} shareCode={shareCode} />

      <ManageMembersModal
        opened={participantsModalOpened}
        onClose={() => setParticipantsModalOpened(false)}
        isOwner={mm.isOwner}
        owners={mm.owners}
        participants={mm.participants}
        requests={mm.requests}
        onApprove={mm.approveRequest}
        onReject={mm.rejectRequest}
        onRemove={(uid) => mm.removeParticipant(uid)}
        roleSuggestions={mm.roleSuggestions}
        onAddRole={mm.addRole}
        onRemoveRole={mm.removeRole}
        onSetContract={mm.setContract}
        onSetWeeklyHours={mm.setWeeklyHoursMax}
        onMakeOwner={mm.makeOwner}
        onRemoveOwner={mm.removeOwner}
        atMemberCap={mm.atMemberCap}
      />
    </Container>
  );
}
